import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    createStoreItem,
    previewCompetitorPricing,
    applyCompetitorPricing,
    fetchStoreItem,
    fetchStoreItemMeta,
    fetchStoreItems,
    removeStoreItemImage,
    toggleStoreItemActive,
    updateStoreItem,
} from "../service/api/adminApi";
import apiClient from "../service/api/apiClient";
import "../css/StoreItemsPage.css";

const initialBulkPricingForm = {
    target_type: "items",
    category_ids: [],
    item_ids: [],
    change_type: "percentage_increase",
    value: "",
    include_inactive: false,
    reason: "",
};

const initialPriceLogFilters = {
    item_id: "",
    per_page: 20,
    page: 1,
};

const initialPriceLogMeta = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: null,
    to: null,
};

const RUPEE = "\u20B9";

const formatPrice = (value) =>
    `${RUPEE}${Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
const emptyForm = {
    id: null,
    name: "",
    desc: "",
    price: "",
    old_price: "",
    item_category_id: "",
    is_recommended: false,
    is_popular: false,
    is_new: true,
    is_veg: "none",
    addon_category_ids: [],
    image: null,
    image_url: "",
    competitor_prices: { swiggy: "", zomato: "" },
};

export default function StoreItemsPage() {
    const { id, itemId } = useParams();
    const navigate = useNavigate();

    const [store, setStore] = useState(null);
    const [items, setItems] = useState([]);
    const [failedImages, setFailedImages] = useState({});
    const [formImageFailed, setFormImageFailed] = useState(false);
    const [meta, setMeta] = useState({ filter_categories: [], item_categories: [], addon_categories: [] });
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        restaurant_total_items: 0,
        restaurant_active_items: 0,
    });

    const [filters, setFilters] = useState({
        q: "",
        item_category_id: "",
        is_active: "",
        is_veg: "",
        flag: "",
        page: 1,
        per_page: 20,
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [toast, setToast] = useState("");
    const [error, setError] = useState("");
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [bulkPricingOpen, setBulkPricingOpen] = useState(false);
    const [showPriceLogs, setShowPriceLogs] = useState(false);
    const [bulkPricingForm, setBulkPricingForm] = useState(initialBulkPricingForm);
    const [bulkPricingPreview, setBulkPricingPreview] = useState([]);
    const [bulkPricingCanApply, setBulkPricingCanApply] = useState(false);
    const [bulkPricingPreviewLoading, setBulkPricingPreviewLoading] = useState(false);
    const [bulkPricingApplying, setBulkPricingApplying] = useState(false);
    const [bulkPricingError, setBulkPricingError] = useState("");
    const [priceLogs, setPriceLogs] = useState([]);
    const [priceLogFilters, setPriceLogFilters] = useState(initialPriceLogFilters);
    const [priceLogMeta, setPriceLogMeta] = useState(initialPriceLogMeta);
    const [priceLogsLoading, setPriceLogsLoading] = useState(false);
    const [priceLogItemName, setPriceLogItemName] = useState("");
    const selectAllRef = useRef(null);
    const [competitorPricingOpen, setCompetitorPricingOpen] = useState(false);
    const [competitorPricingForm, setCompetitorPricingForm] = useState({ swiggy: "", zomato: "" });
    const [competitorPricingPreview, setCompetitorPricingPreview] = useState([]);
    const [competitorPricingPreviewLoading, setCompetitorPricingPreviewLoading] = useState(false);
    const [competitorPricingApplying, setCompetitorPricingApplying] = useState(false);
    const [competitorPricingError, setCompetitorPricingError] = useState("");

    const isEdit = Boolean(form.id);
    const itemsPath = `/stores/${id}/items`;
    const imageFailKey = (item) => `${item.id}-${item.image_url || ""}`;

    const showToast = (msg) => {
        setToast(msg);
        window.clearTimeout(window.__sipToast);
        window.__sipToast = window.setTimeout(() => setToast(""), 2200);
    };

    const loadMeta = async () => {
        const res = await fetchStoreItemMeta(id);
        if (res.success) {
            const data = res?.data?.data || res?.data || {};
            setStore(data.store || null);
            setMeta({
                filter_categories: data.filter_categories || data.item_categories || [],
                item_categories: data.item_categories || [],
                addon_categories: data.addon_categories || [],
            });
        }
    };
    const loadItems = async () => {
        try {
            setLoading(true);
            setError("");
            const res = await fetchStoreItems(id, filters);
            if (res.success) {
                setItems(res.data || []);
                setPagination(res.meta || {});
                setFailedImages({});
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load items");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMeta();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!filters.item_category_id) return;

        const exists = meta.filter_categories.some(
            (category) => Number(category.id) === Number(filters.item_category_id)
        );

        if (!exists) {
            setFilters((previous) => ({
                ...previous,
                item_category_id: "",
                page: 1,
            }));
        }
    }, [meta.filter_categories, filters.item_category_id]);
    useEffect(() => {
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, filters]);

    const filteredTitle = useMemo(() => store?.name || `Restaurant #${id}`, [store, id]);

    const setItemForm = (item) => {
        const addonIds = item.addon_category_ids || item.addon_categories?.map((addon) => addon.id) || [];

        setFormImageFailed(false);

        setForm({
            id: item.id,
            name: item.name || "",
            desc: item.desc || "",
            price: item.price || "",
            old_price: item.old_price || "",
            item_category_id: item.item_category_id || "",
            is_recommended: Boolean(item.is_recommended),
            is_popular: Boolean(item.is_popular),
            is_new: Boolean(item.is_new),
            is_veg: item.is_veg === null ? "none" : Number(item.is_veg) === 1 ? "veg" : "nonveg",
            addon_category_ids: addonIds.map((addonId) => Number(addonId)),
            image: null,
            image_url: item.image_url || "",
            competitor_prices: { swiggy: item.competitor_prices?.swiggy ?? "", zomato: item.competitor_prices?.zomato ?? "" },
        });
    };

    useEffect(() => {
        let alive = true;

        const loadItemForRoute = async () => {
            if (!itemId) {
                setModalOpen(false);
                setForm(emptyForm);
                setFormImageFailed(false);
                return;
            }

            try {
                setError("");
                const res = await fetchStoreItem(id, itemId);
                if (!alive) return;

                const item = res.data?.item || res.data || res.item || res;
                setItemForm(item);
                setModalOpen(true);
            } catch (err) {
                if (!alive) return;
                setModalOpen(false);
                setError(err?.response?.data?.message || "Failed to load item");
                navigate(itemsPath, { replace: true });
            }
        };

        loadItemForRoute();

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, itemId]);

    const openAdd = () => {
        setForm(emptyForm);
        setFormImageFailed(false);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setForm(emptyForm);
        setFormImageFailed(false);
        navigate(itemsPath);
    };

    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    };

    const resetFilters = () => {
        setFilters({
            q: "",
            item_category_id: "",
            is_active: "",
            is_veg: "",
            flag: "",
            page: 1,
            per_page: 20,
        });
    };
    const selectedAddonCategories = form.addon_category_ids
        .map((addonId) => meta.addon_categories.find((option) => Number(option.id) === Number(addonId)))
        .filter(Boolean);

    const availableAddonCategories = meta.addon_categories.filter(
        (option) => !form.addon_category_ids.some((addonId) => Number(addonId) === Number(option.id))
    );

    const addAddonCategory = (event) => {
        const value = Number(event.target.value);
        if (!value) return;

        setForm((prev) => ({
            ...prev,
            addon_category_ids: prev.addon_category_ids.includes(value)
                ? prev.addon_category_ids
                : [...prev.addon_category_ids, value],
        }));
        event.target.value = "";
    };

    const removeAddonCategory = (addonId) => {
        setForm((prev) => ({
            ...prev,
            addon_category_ids: prev.addon_category_ids.filter((idValue) => Number(idValue) !== Number(addonId)),
        }));
    };
    const buildFormData = () => {
        const fd = new FormData();
        fd.append("name", form.name);
        fd.append("desc", form.desc || "");
        fd.append("price", form.price || 0);
        fd.append("old_price", form.old_price || 0);
        fd.append("item_category_id", form.item_category_id);
        fd.append("is_recommended", form.is_recommended ? "1" : "0");
        fd.append("is_popular", form.is_popular ? "1" : "0");
        fd.append("is_new", form.is_new ? "1" : "0");
        fd.append("is_veg", form.is_veg);

        Object.entries(form.competitor_prices || {}).forEach(([platform, value]) => {
            if (value !== "") fd.append(`competitor_prices[${platform}]`, value);
        });

        form.addon_category_ids.forEach((addonId) => {
            fd.append("addon_category_ids[]", addonId);
        });

        if (form.image) {
            fd.append("image", form.image);
        }

        return fd;
    };

    const submitForm = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError("");

            if (isEdit) {
                await updateStoreItem(id, form.id, buildFormData());
                showToast("Item updated successfully");
            } else {
                await createStoreItem(id, buildFormData());
                showToast("Item created successfully");
            }

            setModalOpen(false);
            setForm(emptyForm);
            navigate(itemsPath);
            loadItems();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save item");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (item) => {
        try {
            const next = Number(item.is_active) === 1 ? 0 : 1;
            await toggleStoreItemActive(id, item.id, next);

            setItems((prev) =>
                prev.map((row) =>
                    row.id === item.id ? { ...row, is_active: next } : row
                )
            );

            showToast(next ? "Item enabled" : "Item disabled");
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to update status");
        }
    };

    const removeImage = async () => {
        if (!form.id) return;

        try {
            const res = await removeStoreItemImage(id, form.id);
            setForm((prev) => ({ ...prev, image_url: "", image: null }));
            setFormImageFailed(false);
            showToast(res.message || "Image removed");
            loadItems();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to remove image");
        }
    };

    const visibleItemIds = items.map((item) => String(item.id));
    const allVisibleSelected = visibleItemIds.length > 0 && visibleItemIds.every((itemIdValue) => selectedItemIds.includes(itemIdValue));
    const someVisibleSelected = visibleItemIds.some((itemIdValue) => selectedItemIds.includes(itemIdValue));

    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }, [allVisibleSelected, someVisibleSelected]);

    const clearBulkPreview = () => {
        setBulkPricingPreview([]);
        setBulkPricingCanApply(false);
        setBulkPricingError("");
    };

    const updateBulkPricingForm = (changes) => {
        setBulkPricingForm((current) => ({ ...current, ...changes }));
        clearBulkPreview();
    };

    const openBulkPricing = (useSelection = false) => {
        if (!useSelection || selectedItemIds.length === 0) return;
        setBulkPricingForm({
            ...initialBulkPricingForm,
            item_ids: useSelection ? [...selectedItemIds] : [],
        });
        setBulkPricingPreview([]);
        setBulkPricingCanApply(false);
        setBulkPricingError("");
        setBulkPricingOpen(true);
    };

    const closeBulkPricing = () => {
        setBulkPricingOpen(false);
        setBulkPricingError("");
    };

    const toggleSelectedItem = (itemIdValue) => {
        const value = String(itemIdValue);
        setSelectedItemIds((current) => current.includes(value) ? current.filter((selectedId) => selectedId !== value) : [...current, value]);
    };

    const toggleAllVisible = () => {
        setSelectedItemIds((current) => allVisibleSelected
            ? current.filter((selectedId) => !visibleItemIds.includes(selectedId))
            : [...new Set([...current, ...visibleItemIds])]);
    };

    const buildBulkPricingPayload = () => {
        const payload = {
            target_type: bulkPricingForm.target_type,
            change_type: bulkPricingForm.change_type,
            value: Number(bulkPricingForm.value),
            include_inactive: bulkPricingForm.include_inactive,
        };
        if (bulkPricingForm.target_type === "categories") payload.category_ids = bulkPricingForm.category_ids.map(Number);
        if (bulkPricingForm.target_type === "items") payload.item_ids = bulkPricingForm.item_ids.map(Number);
        if (bulkPricingForm.reason.trim()) payload.reason = bulkPricingForm.reason.trim();
        return payload;
    };

    const validateBulkPricing = () => {
        let message = "";
        if (bulkPricingForm.value === "" || Number.isNaN(Number(bulkPricingForm.value)) || Number(bulkPricingForm.value) <= 0) message = "Enter a valid value greater than zero.";
        else if (bulkPricingForm.target_type === "categories" && !bulkPricingForm.category_ids.length) message = "Select at least one category.";
        else if (bulkPricingForm.target_type === "items" && !bulkPricingForm.item_ids.length) message = "Select at least one item.";
        setBulkPricingError(message);
        return !message;
    };

    const previewBulkPricing = async () => {
        if (!validateBulkPricing()) return;
        try {
            setBulkPricingPreviewLoading(true);
            const response = await apiClient.post(`/stores/${id}/pricing/preview`, buildBulkPricingPayload());
            const data = response?.data?.data || {};
            setBulkPricingPreview(Array.isArray(data.items) ? data.items : []);
            setBulkPricingCanApply(Boolean(data.can_apply));
        } catch (err) {
            console.error(err);
            setBulkPricingPreview([]);
            setBulkPricingCanApply(false);
            setBulkPricingError(err?.response?.data?.message || "Failed to generate pricing preview");
        } finally {
            setBulkPricingPreviewLoading(false);
        }
    };

    const loadPriceLogs = async (nextFilters) => {
        try {
            setPriceLogsLoading(true);
            setError("");
            const params = {
                per_page: nextFilters.per_page || 20,
                page: nextFilters.page || 1,
            };
            if (nextFilters.item_id) params.item_id = nextFilters.item_id;
            const response = await apiClient.get(`/stores/${id}/pricing/logs`, { params });
            setPriceLogs(Array.isArray(response?.data?.data) ? response.data.data : []);
            setPriceLogMeta(response?.data?.meta || initialPriceLogMeta);
            setPriceLogFilters(nextFilters);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || "Failed to load price logs");
        } finally {
            setPriceLogsLoading(false);
        }
    };

    const openAllPriceLogs = () => {
        const nextFilters = { ...initialPriceLogFilters };
        setPriceLogItemName("");
        setShowPriceLogs(true);
        loadPriceLogs(nextFilters);
    };

    const openItemPriceLogs = (item) => {
        const nextFilters = { ...initialPriceLogFilters, item_id: String(item.id), page: 1 };
        setPriceLogItemName(item.name || "");
        setShowPriceLogs(true);
        loadPriceLogs(nextFilters);
    };
    const applyBulkPricing = async () => {
        if (!validateBulkPricing()) return;
        if (!bulkPricingPreview.length || !bulkPricingCanApply) {
            setBulkPricingError("Generate a valid preview before applying prices.");
            return;
        }
        if (!window.confirm(`Apply this price change to ${bulkPricingPreview.length} item(s)?`)) return;
        try {
            setBulkPricingApplying(true);
            const response = await apiClient.post(`/stores/${id}/pricing/apply`, buildBulkPricingPayload());
            showToast(response?.data?.message || `${response?.data?.data?.updated_count || bulkPricingPreview.length} prices updated successfully`);
            setBulkPricingOpen(false);
            setBulkPricingForm(initialBulkPricingForm);
            setBulkPricingPreview([]);
            setBulkPricingCanApply(false);
            setSelectedItemIds([]);
            await loadItems();
            if (showPriceLogs) await loadPriceLogs({ ...priceLogFilters, page: 1 });
        } catch (err) {
            console.error(err);
            setBulkPricingError(err?.response?.data?.message || "Failed to apply pricing");
        } finally {
            setBulkPricingApplying(false);
        }
    };

    const initialCompetitorPricingForm = () => Object.fromEntries(selectedItemIds.map((selectedId) => {
        const item = items.find((row) => String(row.id) === String(selectedId));
        return [String(selectedId), { swiggy: item?.competitor_prices?.swiggy ?? "", zomato: item?.competitor_prices?.zomato ?? "" }];
    }));
    const openCompetitorPricing = () => {
        if (!selectedItemIds.length) return;
        setCompetitorPricingForm(initialCompetitorPricingForm());
        setCompetitorPricingPreview([]);
        setCompetitorPricingError("");
        setCompetitorPricingOpen(true);
    };
    const closeCompetitorPricing = () => { if (!competitorPricingApplying) setCompetitorPricingOpen(false); };
    const selectedCompetitorItems = selectedItemIds.map((selectedId) => items.find((item) => String(item.id) === String(selectedId))).filter(Boolean);
    const updateCompetitorPrice = (itemId, platform, value) => setCompetitorPricingForm((current) => ({ ...current, [String(itemId)]: { ...current[String(itemId)], [platform]: value } }));
    const getCompetitorPreview = () => selectedCompetitorItems.map((item) => {
        const prices = competitorPricingForm[String(item.id)] || {};
        const highestCompetitorPrice = Math.max(Number(prices.swiggy) || 0, Number(prices.zomato) || 0);
        const savedAmount = highestCompetitorPrice - Number(item.price || 0);
        return { id: item.id, name: item.name, current_price: Number(item.price || 0), highest_competitor_price: highestCompetitorPrice, saved_amount: savedAmount };
    });
    const previewCompetitorPrices = async () => {
        if (!selectedCompetitorItems.length) { setCompetitorPricingError("No selected items are available on this page."); return; }
        if (selectedCompetitorItems.some((item) => !competitorPricingForm[String(item.id)]?.swiggy || !competitorPricingForm[String(item.id)]?.zomato)) { setCompetitorPricingError("Enter both Swiggy and Zomato prices for every selected item."); return; }
        try {
            setCompetitorPricingPreviewLoading(true); setCompetitorPricingError("");
            await Promise.all(selectedCompetitorItems.map((item) => previewCompetitorPricing(id, { item_ids: [Number(item.id)], competitor_prices: competitorPricingForm[String(item.id)] })));
            setCompetitorPricingPreview(getCompetitorPreview());
        } catch (err) { setCompetitorPricingPreview([]); setCompetitorPricingError(err?.response?.data?.message || "Failed to generate competitor pricing preview"); }
        finally { setCompetitorPricingPreviewLoading(false); }
    };
    const applyCompetitorPrices = async () => {
        if (!competitorPricingPreview.length) { setCompetitorPricingError("Generate a preview before applying competitor prices."); return; }
        try {
            setCompetitorPricingApplying(true); setCompetitorPricingError("");
            await Promise.all(selectedCompetitorItems.map((item) => applyCompetitorPricing(id, { item_ids: [Number(item.id)], competitor_prices: competitorPricingForm[String(item.id)] })));
            showToast("Competitor prices updated successfully");
            setCompetitorPricingOpen(false); setCompetitorPricingPreview([]); setSelectedItemIds([]); await loadItems();
        } catch (err) { setCompetitorPricingError(err?.response?.data?.message || "Failed to update competitor prices"); }
        finally { setCompetitorPricingApplying(false); }
    };
    const validBulkCount = bulkPricingPreview.filter((item) => item.is_valid).length;
    const totalBulkDifference = bulkPricingPreview.reduce((total, item) => total + Number(item.difference || 0), 0);
    const formatDifference = (value) => {
        const amount = Number(value || 0);
        const formatted = Math.abs(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        if (amount > 0) return `+\u20B9${formatted}`;
        if (amount < 0) return `-\u20B9${formatted}`;
        return `\u20B9${formatted}`;
    };
    return (
        <div className="sip-page">
            {toast && <div className="sip-toast">{toast}</div>}

            <div className="sip-header">
                <div>
                    <h2>Items Management</h2>
                    <p>{filteredTitle}</p>
                </div>

                <div className="sip-header-actions">
                    {showPriceLogs ? (
                        <button type="button" className="sip-secondary-btn" onClick={() => setShowPriceLogs(false)}>Back to Items</button>
                    ) : (
                        <button type="button" className="sip-secondary-btn" onClick={openAllPriceLogs}>Price Logs</button>
                    )}
                    <button type="button" className="sip-primary-btn" onClick={openAdd}>+ Add New Item</button>
                </div>
            </div>

            {error && (
                <div className="sip-error">
                    {error}
                    <button type="button" onClick={() => setError("")}>{"\u00D7"}</button>
                </div>
            )}

            {!showPriceLogs ? (
                <>
                    <div className="sip-filter-card">
                        <input
                            value={filters.q}
                            onChange={(e) => updateFilter("q", e.target.value)}
                            placeholder="Search item name or price..."
                        />

                        <select
                            value={filters.item_category_id}
                            onChange={(e) => updateFilter("item_category_id", e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {meta.filter_categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>

                        <select
                            value={filters.is_active}
                            onChange={(e) => updateFilter("is_active", e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </select>

                        <select
                            value={filters.is_veg}
                            onChange={(e) => updateFilter("is_veg", e.target.value)}
                        >
                            <option value="">All Types</option>
                            <option value="veg">Veg</option>
                            <option value="nonveg">Non-Veg</option>
                            <option value="none">None</option>
                        </select>

                        <select
                            value={filters.flag}
                            onChange={(e) => updateFilter("flag", e.target.value)}
                        >
                            <option value="">All Flags</option>
                            <option value="recommended">Recommended</option>
                            <option value="popular">Popular</option>
                            <option value="new">New</option>
                        </select>

                        <button type="button" onClick={resetFilters}>Reset</button>
                    </div>

                    <div className="sip-card">
                        {selectedItemIds.length > 0 && (
                            <div className="sip-selection-bar"><div className="sip-selection-left"><strong>{selectedItemIds.length} selected</strong></div><div className="sip-selection-actions"><button type="button" className="sip-secondary-btn" onClick={() => setSelectedItemIds([])}>Clear Selection</button><button type="button" className="sip-primary-btn" onClick={() => openBulkPricing(true)}>Bulk Price Update</button><button type="button" className="sip-competitor-btn" onClick={openCompetitorPricing}>Bulk Competitor Price Update</button></div></div>
                        )}
                        {loading ? (
                            <div className="sip-loading">Loading items...</div>
                        ) : items.length === 0 ? (
                            <div className="sip-empty">No items found.</div>
                        ) : (
                            <div className="sip-table-wrap">
                                <table className="sip-table">
                                    <thead>
                                    <tr>
                                        <th className="sip-table-checkbox"><input ref={selectAllRef} type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} aria-label="Select all visible items" /></th>
                                        <th>Image</th>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>Price</th>
                                        <th>Attributes</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {items.map((item) => {
                                        const failKey = imageFailKey(item);

                                        return (
                                            <tr key={item.id}>
                                                <td className="sip-table-checkbox"><input type="checkbox" checked={selectedItemIds.includes(String(item.id))} onChange={() => toggleSelectedItem(item.id)} aria-label={`Select ${item.name}`} /></td>
                                                <td>
                                                    {item.image_url && !failedImages[failKey] ? (
                                                        <img
                                                            className="sip-item-img"
                                                            src={item.image_url}
                                                            alt=""
                                                            loading="lazy"
                                                            onError={() =>
                                                                setFailedImages((prev) => ({
                                                                    ...prev,
                                                                    [failKey]: true,
                                                                }))
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="sip-no-img">No Image</div>
                                                    )}
                                                </td>

                                                <td>
                                                    <strong>{item.name}</strong>
                                                    <small>{item.restaurant?.name}</small>
                                                </td>

                                                <td>{item.item_category?.name || "--"}</td>

                                                <td>
                                                    <strong>{formatPrice(item.price)}</strong>
                                                    {Number(item.old_price) > 0 && <small className="sip-old-price">{formatPrice(item.old_price)}</small>}
                                                    {Object.entries(item.competitor_prices || {}).filter(([platform, value]) => ["swiggy", "zomato"].includes(platform) && value !== null && value !== "").length > 0 && <div className="sip-item-competitors"><small>Competitors:</small>{Object.entries(item.competitor_prices || {}).filter(([platform, value]) => ["swiggy", "zomato"].includes(platform) && value !== null && value !== "").map(([platform, value]) => <span key={platform}>{platform} {formatPrice(value)}</span>)}</div>}
                                                </td>

                                                <td>
                                                    <div className="sip-badges">
                                                        {item.is_veg === 1 && <span className="veg">Veg</span>}
                                                        {item.is_veg === 0 && <span className="nonveg">Non-Veg</span>}
                                                        {item.is_recommended === 1 && <span>Recommended</span>}
                                                        {item.is_popular === 1 && <span>Popular</span>}
                                                        {item.is_new === 1 && <span>New</span>}
                                                    </div>
                                                </td>

                                                <td>
                                                    <button
                                                        type="button"
                                                        className={`sip-switch ${item.is_active ? "on" : "off"}`}
                                                        onClick={() => toggleActive(item)}
                                                    >
                                                        <span />
                                                    </button>
                                                </td>

                                                <td>{item.created_at || "--"}</td>

                                                <td className="sip-right">
                                                    <div className="sip-action-group">
                                                        <button type="button" className="sip-edit-btn" onClick={() => navigate(`/stores/${id}/items/${item.id}`)}>Edit</button>
                                                        <button type="button" className="sip-log-btn" onClick={() => openItemPriceLogs(item)}>Logs</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="sip-pagination">

                            <div className="sip-total-count">
                                Total Items: <strong>{pagination.restaurant_total_items || 0}</strong>
                            </div>

                            <div className="sip-pagination-right">
                                <button
                                    disabled={pagination.current_page <= 1}
                                    onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
                                >
                                    Previous
                                </button>

                                <span>
      Page {pagination.current_page || 1} of {pagination.last_page || 1}
    </span>

                                <button
                                    disabled={pagination.current_page >= pagination.last_page}
                                    onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
                                >
                                    Next
                                </button>
                            </div>

                        </div>
                    </div>

                </>
            ) : (
                <div className="sip-log-page">
                    <div className="sip-log-head">
                        <h3>Price Update Logs</h3>
                        <p className="sip-log-context">{priceLogItemName ? `Showing price changes for: ${priceLogItemName}` : "Manage all item price changes for this store"}</p>
                    </div>          <div className="sip-log-card">
                    {priceLogsLoading ? <div className="sip-log-loading">Loading price logs...</div> : (
                        <div className="sip-log-table-wrap"><table className="sip-log-table"><thead><tr><th>Date</th><th>Item</th><th>Old Price</th><th>New Price</th><th>Difference</th><th>Direction</th><th>Changed By</th><th>Message</th><th>Action</th></tr></thead><tbody>{priceLogs.map((log) => { const date = new Date(log.created_at); const amount = Number(log.difference || 0); return <tr key={log.id}><td>{Number.isNaN(date.getTime()) ? log.created_at : date.toLocaleString()}</td><td>{log.item_name}</td><td>{formatPrice(log.old_price)}</td><td>{formatPrice(log.new_price)}</td><td><span className={`sip-log-difference ${amount > 0 ? "increase" : amount < 0 ? "decrease" : ""}`}>{formatDifference(amount)}</span></td><td><span className={`sip-log-direction ${log.direction}`}>{log.direction}</span></td><td>{log.changed_by || "--"}</td><td>{log.message || "--"}</td><td><button type="button" className="sip-edit-btn" onClick={() => navigate(`/stores/${id}/items/${log.item_id}`)}>Edit Item</button></td></tr>; })}{!priceLogs.length && <tr><td colSpan="9" className="sip-log-empty">No price logs found.</td></tr>}</tbody></table></div>
                    )}
                    <div className="sip-log-pagination"><span>Showing {priceLogMeta.from ?? 0} to {priceLogMeta.to ?? 0} of {priceLogMeta.total} entries</span><div><button type="button" disabled={priceLogsLoading || priceLogMeta.current_page <= 1} onClick={() => loadPriceLogs({ ...priceLogFilters, page: priceLogMeta.current_page - 1 })}>Previous</button><button type="button" disabled={priceLogsLoading || priceLogMeta.current_page >= priceLogMeta.last_page} onClick={() => loadPriceLogs({ ...priceLogFilters, page: priceLogMeta.current_page + 1 })}>Next</button></div></div>
                </div>
                </div>
            )}
            {bulkPricingOpen && (
                <div className="sip-bulk-modal-overlay" role="presentation" onMouseDown={closeBulkPricing}>
                    <div className="sip-bulk-modal" role="dialog" aria-modal="true" aria-labelledby="sip-bulk-title" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="sip-bulk-modal-head">
                            <div>
                                <h3 id="sip-bulk-title">Bulk Price Update</h3>
                                <p>Update prices for selected items.</p>
                            </div>
                            <button type="button" className="sip-bulk-modal-close" onClick={closeBulkPricing} disabled={bulkPricingApplying}>{"\u00D7"}</button>
                        </div>

                        <div className="sip-bulk-modal-body">
                            <div className="sip-bulk-summary">
                                <div className="sip-bulk-summary-row"><span>Target Type</span><strong>Selected Items</strong></div>
                                <div className="sip-bulk-summary-row"><span>Selected Items</span><strong>{bulkPricingForm.item_ids.length}</strong></div>
                                <div className="sip-bulk-summary-row"><span>Include Inactive</span><strong>{bulkPricingForm.include_inactive ? "Yes" : "No"}</strong></div>
                            </div>

                            <div className="sip-bulk-field">
                                <div className="sip-bulk-selected-head"><label>Selected items</label><button type="button" className="sip-secondary-btn" onClick={() => updateBulkPricingForm({ item_ids: [] })}>Clear Selected Items</button></div>
                                <div className="sip-bulk-items">
                                    {bulkPricingForm.item_ids.map((selectedId) => {
                                        const item = items.find((row) => String(row.id) === String(selectedId));
                                        return (
                                            <div className="sip-bulk-item-row" key={selectedId}>
                                                <div className="sip-bulk-item-info"><strong>{item?.name || `Item #${selectedId}`}</strong><small>{item?.item_category?.name || (item ? "Uncategorized" : "Not on current page")}</small></div>
                                                {item && <span className="sip-bulk-item-price">{formatPrice(item.price)}</span>}
                                                <button type="button" onClick={() => updateBulkPricingForm({ item_ids: bulkPricingForm.item_ids.filter((value) => String(value) !== String(selectedId)) })}>Remove</button>
                                            </div>
                                        );
                                    })}
                                    {!bulkPricingForm.item_ids.length && <div className="sip-log-empty">No items selected.</div>}
                                </div>
                            </div>

                            <div className="sip-bulk-form-grid">
                                <div className="sip-bulk-field"><label htmlFor="bulkChangeType">Change operation</label><select id="bulkChangeType" value={bulkPricingForm.change_type} onChange={(e) => updateBulkPricingForm({ change_type: e.target.value })}><option value="percentage_increase">Percentage Increase</option><option value="percentage_decrease">Percentage Decrease</option><option value="fixed_increase">Fixed Increase</option><option value="fixed_decrease">Fixed Decrease</option><option value="exact_price">Set Exact Price</option></select></div>
                                <div className="sip-bulk-field"><label htmlFor="bulkPricingValue">{bulkPricingForm.change_type.startsWith("percentage") ? "Percentage Value" : "Amount"}</label><div className="sip-bulk-value"><span>{bulkPricingForm.change_type.startsWith("percentage") ? "%" : "\u20B9"}</span><input id="bulkPricingValue" type="number" min="0.01" step="0.01" value={bulkPricingForm.value} onChange={(e) => updateBulkPricingForm({ value: e.target.value })} /></div></div>
                                <div className="sip-bulk-field sip-bulk-switch-field"><label>Include inactive items</label><button type="button" className={`sip-switch ${bulkPricingForm.include_inactive ? "on" : "off"}`} onClick={() => updateBulkPricingForm({ include_inactive: !bulkPricingForm.include_inactive })}><span /></button></div>
                                <div className="sip-bulk-field"><label htmlFor="bulkPricingReason">Reason</label><input id="bulkPricingReason" maxLength={255} value={bulkPricingForm.reason} onChange={(e) => { setBulkPricingForm((current) => ({ ...current, reason: e.target.value })); clearBulkPreview(); }} placeholder="Optional reason for this bulk price update" /></div>
                            </div>

                            {bulkPricingError && <div className="sip-bulk-error">{bulkPricingError}</div>}
                            {bulkPricingPreview.length > 0 && <div className="sip-bulk-preview"><div className="sip-bulk-summary-cards"><div className="sip-bulk-summary-card"><span>Affected Items</span><strong>{bulkPricingPreview.length}</strong></div><div className="sip-bulk-summary-card"><span>Valid Items</span><strong>{validBulkCount}</strong></div><div className="sip-bulk-summary-card"><span>Invalid Items</span><strong>{bulkPricingPreview.length - validBulkCount}</strong></div><div className="sip-bulk-summary-card"><span>Total Difference</span><strong>{formatDifference(totalBulkDifference)}</strong></div></div><div className="sip-bulk-table-wrap"><table className="sip-bulk-table"><thead><tr><th>Item</th><th>Current Price</th><th>New Price</th><th>Difference</th><th>Status</th></tr></thead><tbody>{bulkPricingPreview.map((item) => { const difference = Number(item.difference || 0); return <tr key={item.id}><td>{item.name}</td><td>{formatPrice(item.current_price)}</td><td>{formatPrice(item.new_price)}</td><td><span className={`sip-bulk-diff ${difference > 0 ? "increase" : difference < 0 ? "decrease" : ""}`}>{formatDifference(difference)}</span></td><td><span className={`sip-bulk-badge ${item.is_valid ? "valid" : "invalid"}`}>{item.is_valid ? "Valid" : "Invalid"}</span>{!item.is_valid && Number(item.new_price) <= 0 && <small>Price must be greater than zero</small>}</td></tr>; })}</tbody></table></div></div>}
                        </div>

                        <div className="sip-bulk-modal-footer"><button type="button" className="sip-secondary-btn" onClick={closeBulkPricing}>Cancel</button><button type="button" className="sip-secondary-btn" disabled={bulkPricingPreviewLoading} onClick={previewBulkPricing}>{bulkPricingPreviewLoading ? "Generating..." : "Generate Preview"}</button><button type="button" className="sip-primary-btn" disabled={!bulkPricingPreview.length || !bulkPricingCanApply || bulkPricingApplying} onClick={applyBulkPricing}>{bulkPricingApplying ? "Applying..." : "Apply Prices"}</button></div>
                    </div>
                </div>
            )}
            {competitorPricingOpen && (
                <div className="sip-bulk-modal-overlay" role="presentation" onMouseDown={closeCompetitorPricing}>
                    <div className="sip-bulk-modal sip-competitor-modal" role="dialog" aria-modal="true" aria-labelledby="sip-competitor-title" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="sip-bulk-modal-head"><div><h3 id="sip-competitor-title">Bulk Competitor Price Update</h3><p>Set platform prices for each selected item.</p></div><button type="button" className="sip-bulk-modal-close" onClick={closeCompetitorPricing} disabled={competitorPricingApplying}>{"×"}</button></div>
                        <div className="sip-bulk-modal-body"><div className="sip-competitor-selected-card"><span>{selectedCompetitorItems.length} Items Selected</span><strong>Swiggy & Zomato</strong></div><div className="sip-competitor-item-list">{selectedCompetitorItems.map((item) => { const prices = competitorPricingForm[String(item.id)] || {}; return <div className="sip-competitor-item-card" key={item.id}><div><strong>{item.name}</strong><small>Current App Price: {formatPrice(item.price)}</small></div><label>Swiggy Price<input type="number" min="0" step="0.01" value={prices.swiggy ?? ""} onChange={(event) => updateCompetitorPrice(item.id, "swiggy", event.target.value)} placeholder="Enter Swiggy price" /></label><label>Zomato Price<input type="number" min="0" step="0.01" value={prices.zomato ?? ""} onChange={(event) => updateCompetitorPrice(item.id, "zomato", event.target.value)} placeholder="Enter Zomato price" /></label></div>; })}</div>{competitorPricingError && <div className="sip-bulk-error">{competitorPricingError}</div>}{competitorPricingPreviewLoading ? <div className="sip-competitor-loading">Generating preview...</div> : competitorPricingPreview.length > 0 && <div className="sip-competitor-preview"><div className="sip-competitor-table-wrap"><table className="sip-competitor-table"><thead><tr><th>Item Name</th><th>Current Price</th><th>Highest Competitor Price</th><th>Saved Amount</th><th>Recommendation</th></tr></thead><tbody>{competitorPricingPreview.map((item) => <tr key={item.id}><td>{item.name}</td><td>{formatPrice(item.current_price)}</td><td><span className="sip-competitor-highest">{formatPrice(item.highest_competitor_price)}</span></td><td><span className={`sip-competitor-saved ${item.saved_amount > 0 ? "positive" : ""}`}>{formatDifference(item.saved_amount)}</span></td><td>{item.saved_amount > 0 ? `Customers save ${formatPrice(item.saved_amount)} compared to other platforms` : "No customer saving against competitor prices"}</td></tr>)}</tbody></table></div></div>}</div>
                        <div className="sip-bulk-modal-footer"><button type="button" className="sip-secondary-btn" onClick={closeCompetitorPricing} disabled={competitorPricingApplying}>Cancel</button><button type="button" className="sip-secondary-btn" onClick={previewCompetitorPrices} disabled={competitorPricingPreviewLoading || competitorPricingApplying}>{competitorPricingPreviewLoading ? "Generating..." : "Generate Preview"}</button><button type="button" className="sip-primary-btn" onClick={applyCompetitorPrices} disabled={competitorPricingApplying || !competitorPricingPreview.length}>{competitorPricingApplying ? "Applying..." : "Apply Competitor Prices"}</button></div>
                    </div>
                </div>
            )}      {modalOpen && (
            <div className="sip-modal-overlay">
                <div className="sip-modal">
                    <div className="sip-modal-head">
                        <h3>{isEdit ? "Edit Item" : "Add New Item"}</h3>
                        <button type="button" onClick={closeModal}>{"\u00D7"}</button>
                    </div>

                    <form onSubmit={submitForm} className="sip-form">
                        <div className="sip-form-grid">
                            <label>
                                Item Name *
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </label>

                            <label>
                                Category *
                                <select
                                    required
                                    value={form.item_category_id}
                                    onChange={(e) => setForm({ ...form, item_category_id: e.target.value })}
                                >
                                    <option value="">Select Category</option>
                                    {meta.item_categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                Price *
                                <input
                                    required
                                    type="number"
                                    step="0.01"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                />
                            </label>

                            <label>
                                Mark Price
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.old_price}
                                    onChange={(e) => setForm({ ...form, old_price: e.target.value })}
                                />
                            </label>
                        </div>

                        <div className="sip-competitor-edit-section">
                            <div className="sip-section-title">Competitor Prices</div>
                            <div className="sip-competitor-grid">
                                {[["swiggy", "Swiggy"], ["zomato", "Zomato"]].map(([platform, label]) => (
                                    <label className="sip-competitor-card" key={platform}>
                                        <span>{label}</span>
                                        <input type="number" min="0" step="0.01" value={form.competitor_prices?.[platform] ?? ""} onChange={(event) => setForm({ ...form, competitor_prices: { ...form.competitor_prices, [platform]: event.target.value } })} placeholder="Enter price" />
                                    </label>
                                ))}
                            </div>
                        </div>
                        <label>
                            Description
                            <textarea
                                rows="4"
                                value={form.desc}
                                onChange={(e) => setForm({ ...form, desc: e.target.value })}
                            />
                        </label>

                        <div className="sip-toggle-list">
                            {[
                                ["is_recommended", "Is Recommended?"],
                                ["is_popular", "Is Popular?"],
                                ["is_new", "Is New?"],
                            ].map(([key, label]) => (
                                <div className="sip-toggle-row" key={key}>
                                    <span>{label}</span>
                                    <button
                                        type="button"
                                        className={`sip-switch ${form[key] ? "on" : "off"}`}
                                        onClick={() => setForm({ ...form, [key]: !form[key] })}
                                    >
                                        <span />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="sip-radio-row">
                            <label><input type="radio" name="veg" checked={form.is_veg === "veg"} onChange={() => setForm({ ...form, is_veg: "veg" })} /> Veg</label>
                            <label><input type="radio" name="veg" checked={form.is_veg === "nonveg"} onChange={() => setForm({ ...form, is_veg: "nonveg" })} /> Non-Veg</label>
                            <label><input type="radio" name="veg" checked={form.is_veg === "none"} onChange={() => setForm({ ...form, is_veg: "none" })} /> None</label>
                        </div>

                        <div className="sip-form-row sip-form-row-top">
                            <label htmlFor="addonCategoryAdd">Item's Addon Categories</label>

                            <div className="sip-tag-selector">
                                <div className="sip-tag-box">
                                    {selectedAddonCategories.length > 0 ? (
                                        selectedAddonCategories.map((addon) => (
                                            <span className="sip-tag" key={addon.id}>
            {addon.name}
                                                <button
                                                    type="button"
                                                    onClick={() => removeAddonCategory(addon.id)}
                                                    aria-label={`Remove ${addon.name}`}
                                                >
              {"\u00D7"}
            </button>
          </span>
                                        ))
                                    ) : (
                                        <span className="sip-tag-empty">No addon categories selected</span>
                                    )}
                                </div>

                                <select
                                    id="addonCategoryAdd"
                                    value=""
                                    onChange={addAddonCategory}
                                    className="sip-tag-add-select"
                                >
                                    <option value="">Add addon category</option>
                                    {availableAddonCategories.map((addon) => (
                                        <option key={addon.id} value={addon.id}>
                                            {addon.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="sip-image-row">
                            <div>
                                {form.image_url && !formImageFailed ? (
                                    <img
                                        src={form.image_url}
                                        alt=""
                                        onError={() => setFormImageFailed(true)}
                                    />
                                ) : (
                                    <div className="sip-modal-no-img">No Image</div>
                                )}
                                {isEdit && form.image_url && (
                                    <button type="button" onClick={removeImage}>
                                        Remove Image
                                    </button>
                                )}
                            </div>

                            <label>
                                Image
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        setFormImageFailed(false);
                                        setForm({
                                            ...form,
                                            image: e.target.files?.[0] || null,
                                            image_url: e.target.files?.[0]
                                                ? URL.createObjectURL(e.target.files[0])
                                                : form.image_url,
                                        });
                                    }}
                                />
                                <small>Recommended size 486x355</small>
                            </label>
                        </div>

                        <div className="sip-modal-actions">
                            <button type="button" onClick={closeModal}>{"\u00D7"}</button>
                            <button type="submit" disabled={saving}>
                                {saving ? "Saving..." : "Save Item"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}
        </div>
    );
}